import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, BehaviorSubject } from 'rxjs';

export interface NavigationItem {
  id: string;
  title: string;
  type: 'item' | 'collapse' | 'group';
  translate?: string;
  icon?: string;
  hidden?: boolean;
  url?: string;
  classes?: string;
  exactMatch?: boolean;
  external?: boolean;
  target?: boolean;
  breadcrumbs?: boolean;
  badge?: {
    title?: string;
    type?: string;
  };
  children?: NavigationItem[];
  role?: string[];
  disabled?: boolean;
  isMainParent?: boolean; // specify if item is main parent
}

@Injectable({
  providedIn: 'root'
})
export class NavService implements OnDestroy {
  private unsubscriber: Subject<any> = new Subject();

  constructor(private router: Router) {}

  ngOnDestroy() {
    this.unsubscriber.next;
    this.unsubscriber.complete();
  }

  UpdateMenuList(): void {
    const d: any = localStorage.getItem('currentUser');
    const storedData: any = JSON.parse(d);
    // console.log(storedData);
    if (!storedData || !storedData.user.menu_rights) {
      this.items.next([]);
      return;
    }

    const menuRights: any[] = storedData.user.menu_rights;
    this._injectChecklistItem(menuRights);
    this._injectScheduledTasksItem(menuRights);
    this._injectTechnicianMasterItem(menuRights);
    this._injectLineMasterItem(menuRights);
    this._injectChecklistMasterItem(menuRights);
    this._injectActivityTemplateItem(menuRights);
    this._injectDefectTypeMasterItem(menuRights);
    this._injectAmcGroup(menuRights);
    this._injectMntDashboardItem(menuRights);
    this._injectAssetScanItem(menuRights);
    this._filterOutDepartmentItem(menuRights);
    this._filterOutAssetAllocationItem(menuRights);
    this.items.next(menuRights);
  }

  /** Injects the Checklist item under the Scheduler sub-group (frontend-only, no DB change). */
  private _injectChecklistItem(groups: any[]): void {
    const CHECKLIST_ID = 'checklist-static';
    const CHECKLIST_ITEM = {
      id: CHECKLIST_ID,
      add_rights: true,
      edit_rights: true,
      delete_rights: true,
      view_rights: true,
      homepage: false,
      name: 'Checklist',
      url: '/mnt/trans/scheduler/checklist',
      unique_id: CHECKLIST_ID,
      type: 'item',
      icon: 'feather icon-check-square'
    };

    for (const group of groups) {
      const subGroups: any[] = group.children || [];
      for (const sub of subGroups) {
        if ((sub.name || '').toLowerCase() === 'scheduler') {
          const children: any[] = sub.children || [];
          const alreadyAdded = children.some((c: any) => c.unique_id === CHECKLIST_ID || c.url === CHECKLIST_ITEM.url);
          if (!alreadyAdded) {
            children.push(CHECKLIST_ITEM);
          }
          sub.children = children;
          break;
        }
      }
    }
  }

  /** Injects the Scheduled Tasks item under the Scheduler sub-group (frontend-only, no DB change). */
  private _injectScheduledTasksItem(groups: any[]): void {
    const ITEM_ID = 'scheduled-tasks-static';
    const ITEM = {
      id: ITEM_ID,
      add_rights: false,
      edit_rights: false,
      delete_rights: false,
      view_rights: true,
      homepage: false,
      name: 'Scheduled Tasks',
      url: '/mnt/trans/scheduler/tasks',
      unique_id: ITEM_ID,
      type: 'item',
      icon: 'feather icon-calendar'
    };

    for (const group of groups) {
      const subGroups: any[] = group.children || [];
      for (const sub of subGroups) {
        if ((sub.name || '').toLowerCase() === 'scheduler') {
          const children: any[] = sub.children || [];
          const alreadyAdded = children.some((c: any) => c.unique_id === ITEM_ID || c.url === ITEM.url);
          if (!alreadyAdded) {
            // Insert after 'Schedule List' (first child) so order is: Schedule List → Scheduled Tasks → Checklist → …
            const schedListIdx = children.findIndex((c: any) =>
              (c.url || '').includes('/scheduler/list') || (c.name || '').toLowerCase() === 'schedule list'
            );
            if (schedListIdx >= 0) {
              children.splice(schedListIdx + 1, 0, ITEM);
            } else {
              children.unshift(ITEM);
            }
          }
          sub.children = children;
          break;
        }
      }
    }
  }

  /** Injects the Line Master item under the Maintanance sub-group */
  private _injectLineMasterItem(groups: any[]): void {
    const LINE_MASTER_ID = 'line-master-static';
    const LINE_MASTER_ITEM = {
      id: LINE_MASTER_ID,
      add_rights: true,
      edit_rights: true,
      delete_rights: true,
      view_rights: true,
      homepage: false,
      name: 'Line Master',
      url: '/mnt/masters/line/list',
      unique_id: LINE_MASTER_ID,
      type: 'item',
      icon: 'feather icon-layers'
    };

    for (const group of groups) {
      const subGroups: any[] = group.children || [];
      for (const sub of subGroups) {
        if ((sub.name || '').toLowerCase() === 'maintanance') {
          const children: any[] = sub.children || [];
          const alreadyAdded = children.some((c: any) => c.unique_id === LINE_MASTER_ID || c.url === LINE_MASTER_ITEM.url);
          if (!alreadyAdded) {
            children.push(LINE_MASTER_ITEM);
          }
          sub.children = children;
          break;
        }
      }
    }
  }

  /** Injects the Technician Master item under the Maintanance sub-group */
  private _injectTechnicianMasterItem(groups: any[]): void {
    const TECHNICIAN_ID = 'technician-static';
    const TECHNICIAN_ITEM = {
      id: TECHNICIAN_ID,
      add_rights: true,
      edit_rights: true,
      delete_rights: true,
      view_rights: true,
      homepage: false,
      name: 'Technician Master',
      url: '/mnt/masters/technician/list',
      unique_id: TECHNICIAN_ID,
      type: 'item',
      icon: 'feather icon-user'
    };

    for (const group of groups) {
      const subGroups: any[] = group.children || [];
      for (const sub of subGroups) {
        if ((sub.name || '').toLowerCase() === 'maintanance') {
          const children: any[] = sub.children || [];
          const alreadyAdded = children.some((c: any) => c.unique_id === TECHNICIAN_ID || c.url === TECHNICIAN_ITEM.url);
          if (!alreadyAdded) {
            children.push(TECHNICIAN_ITEM);
          }
          sub.children = children;
          break;
        }
      }
    }
  }

  /** Injects the Checklist Master item under the Maintanance sub-group (frontend-only, no DB change). */
  private _injectChecklistMasterItem(groups: any[]): void {
    const CHECKLIST_MASTER_ID = 'checklist-master-static';
    const CHECKLIST_MASTER_ITEM = {
      id: CHECKLIST_MASTER_ID,
      add_rights: true,
      edit_rights: true,
      delete_rights: true,
      view_rights: true,
      homepage: false,
      name: 'Checklist Master',
      url: '/mnt/masters/checklist-master/list',
      unique_id: CHECKLIST_MASTER_ID,
      type: 'item',
      icon: 'feather icon-list'
    };

    for (const group of groups) {
      const subGroups: any[] = group.children || [];
      for (const sub of subGroups) {
        if ((sub.name || '').toLowerCase() === 'maintanance') {
          const children: any[] = sub.children || [];
          const alreadyAdded = children.some((c: any) => c.unique_id === CHECKLIST_MASTER_ID || c.url === CHECKLIST_MASTER_ITEM.url);
          if (!alreadyAdded) {
            children.push(CHECKLIST_MASTER_ITEM);
          }
          sub.children = children;
          break;
        }
      }
    }
  }

  /** Injects the Activity Template item under the Maintanance sub-group (frontend-only, no DB change). */
  private _injectActivityTemplateItem(groups: any[]): void {
    const ACTIVITY_TEMPLATE_ID = 'activity-template-static';
    const ACTIVITY_TEMPLATE_ITEM = {
      id: ACTIVITY_TEMPLATE_ID,
      add_rights: true,
      edit_rights: true,
      delete_rights: true,
      view_rights: true,
      homepage: false,
      name: 'Activity Template',
      url: '/mnt/masters/activity-template/list',
      unique_id: ACTIVITY_TEMPLATE_ID,
      type: 'item',
      icon: 'feather icon-clipboard'
    };

    for (const group of groups) {
      const subGroups: any[] = group.children || [];
      for (const sub of subGroups) {
        if ((sub.name || '').toLowerCase() === 'maintanance') {
          const children: any[] = sub.children || [];
          const alreadyAdded = children.some((c: any) => c.unique_id === ACTIVITY_TEMPLATE_ID || c.url === ACTIVITY_TEMPLATE_ITEM.url);
          if (!alreadyAdded) {
            children.push(ACTIVITY_TEMPLATE_ITEM);
          }
          sub.children = children;
          break;
        }
      }
    }
  }

  /** Injects the Defect Type Master item under the Maintanance sub-group (frontend-only, no DB change). */
  private _injectDefectTypeMasterItem(groups: any[]): void {
    const DEFECT_TYPE_ID = 'defect-type-static';
    const DEFECT_TYPE_ITEM = {
      id: DEFECT_TYPE_ID,
      add_rights: true,
      edit_rights: true,
      delete_rights: true,
      view_rights: true,
      homepage: false,
      name: 'Defect Type Master',
      url: '/mnt/masters/defect-type/list',
      unique_id: DEFECT_TYPE_ID,
      type: 'item',
      icon: 'feather icon-alert-circle'
    };

    for (const group of groups) {
      const subGroups: any[] = group.children || [];
      for (const sub of subGroups) {
        if ((sub.name || '').toLowerCase() === 'maintanance') {
          const children: any[] = sub.children || [];
          const alreadyAdded = children.some((c: any) => c.unique_id === DEFECT_TYPE_ID || c.url === DEFECT_TYPE_ITEM.url);
          if (!alreadyAdded) {
            children.push(DEFECT_TYPE_ITEM);
          }
          sub.children = children;
          break;
        }
      }
    }
  }

  /** Injects the AMC collapse group (List / Add / Report) as a sibling of the Scheduler sub-group (frontend-only, no DB change). */
  private _injectAmcGroup(groups: any[]): void {
    const AMC_GROUP_ID = 'amc-static';

    // All four roles are listed so isEnabled resolves to true for every user
    // regardless of which role string the auth service resolves to.
    const ALL_ROLES = ['Admin', 'admin', 'User', 'user'];

    const baseRights = {
      add_rights: true,
      edit_rights: true,
      delete_rights: true,
      view_rights: true,
      homepage: false,
      // role must be non-empty so the nav-item/nav-collapse isEnabled check
      // enters the item.role branch instead of the parentRole branch.
      role: ALL_ROLES
    };
    const AMC_GROUP = {
      id: AMC_GROUP_ID,
      ...baseRights,
      name: 'AMC',
      unique_id: AMC_GROUP_ID,
      type: 'collapse',
      icon: 'feather icon-shield',
      // isMainParent bypasses the allowedFromParent parent-role guard so the
      // collapse header itself is enabled even when parentRole is unknown.
      isMainParent: true,
      children: [
        {
          id: 'amc-list-static',
          ...baseRights,
          name: 'AMC List',
          url: '/mnt/trans/amc/list',
          unique_id: 'amc-list-static',
          type: 'item',
          icon: 'feather icon-list'
        },
        {
          id: 'amc-add-static',
          ...baseRights,
          name: 'Add AMC',
          url: '/mnt/trans/amc/add',
          unique_id: 'amc-add-static',
          type: 'item',
          icon: 'feather icon-plus'
        },
        {
          id: 'amc-report-static',
          ...baseRights,
          name: 'AMC Report',
          url: '/mnt/trans/amc/report',
          unique_id: 'amc-report-static',
          type: 'item',
          icon: 'feather icon-bar-chart-2'
        }
      ]
    };

    for (const group of groups) {
      const subGroups: any[] = group.children || [];
      const hasScheduler = subGroups.some((s: any) => (s.name || '').toLowerCase() === 'scheduler');
      if (hasScheduler) {
        const alreadyAdded = subGroups.some((s: any) => s.unique_id === AMC_GROUP_ID || (s.name || '').toLowerCase() === 'amc');
        if (!alreadyAdded) {
          subGroups.push(AMC_GROUP);
        }
        group.children = subGroups;
        break;
      }
    }
  }

  /** Injects the Maintenance Dashboard item as a sibling after AMC (frontend-only, no DB change). */
  private _injectMntDashboardItem(groups: any[]): void {
    const DASHBOARD_ID = 'mnt-dashboard-static';
    const ALL_ROLES = ['Admin', 'admin', 'User', 'user'];
    const DASHBOARD_ITEM = {
      id: DASHBOARD_ID,
      add_rights: true,
      edit_rights: true,
      delete_rights: true,
      view_rights: true,
      homepage: false,
      role: ALL_ROLES,
      name: 'Maintenance Dashboard',
      url: '/mnt/trans/dashboard',
      unique_id: DASHBOARD_ID,
      type: 'item',
      icon: 'feather icon-activity',
      isMainParent: true
    };

    for (const group of groups) {
      const subGroups: any[] = group.children || [];
      const hasScheduler = subGroups.some((s: any) => (s.name || '').toLowerCase() === 'scheduler');
      if (hasScheduler) {
        const alreadyAdded = subGroups.some(
          (s: any) => s.unique_id === DASHBOARD_ID || s.url === DASHBOARD_ITEM.url
        );
        if (!alreadyAdded) {
          subGroups.push(DASHBOARD_ITEM);
        }
        group.children = subGroups;
        break;
      }
    }
  }


  /**
   * Injects the Asset Scan item as the first child of the Assets sub-group
   * (frontend-only, no DB change).
   * Route: /mnt/trans/assets/scan
   */
  private _injectAssetScanItem(groups: any[]): void {
    const ASSET_SCAN_ID = 'asset-scan-static';
    const ALL_ROLES = ['Admin', 'admin', 'User', 'user'];
    const ASSET_SCAN_ITEM = {
      id: ASSET_SCAN_ID,
      add_rights: true,
      edit_rights: true,
      delete_rights: true,
      view_rights: true,
      homepage: false,
      role: ALL_ROLES,
      name: 'Scan',
      url: '/mnt/trans/assets/scan',
      unique_id: ASSET_SCAN_ID,
      type: 'item',
      icon: 'feather icon-camera',
      isMainParent: true
    };

    for (const group of groups) {
      const subGroups: any[] = group.children || [];
      for (const sub of subGroups) {
        if ((sub.name || '').toLowerCase() === 'assets') {
          const children: any[] = sub.children || [];
          const alreadyAdded = children.some(
            (c: any) => c.unique_id === ASSET_SCAN_ID || c.url === ASSET_SCAN_ITEM.url
          );
          if (!alreadyAdded) {
            // unshift places Scan as the very first item under Assets
            children.unshift(ASSET_SCAN_ITEM);
          }
          sub.children = children;
          return;
        }
      }
    }
  }


  /** Filters out the Department item from the Maintanance sub-group (frontend-only, no DB change). */
  private _filterOutDepartmentItem(groups: any[]): void {
    for (const group of groups) {
      const subGroups: any[] = group.children || [];
      for (const sub of subGroups) {
        if ((sub.name || '').toLowerCase() === 'maintanance') {
          const children: any[] = sub.children || [];
          sub.children = children.filter((child: any) => 
            (child.name || '').toLowerCase() !== 'department' && 
            (child.url || '') !== '/mnt/masters/department/list'
          );
          break;
        }
      }
    }
  }

  /** Filters out Asset Allocation from navigation across all sub-groups (frontend-only / refresh-safe). */
  private _filterOutAssetAllocationItem(groups: any[]): void {
    for (const group of groups) {
      const subGroups: any[] = group.children || [];
      for (const sub of subGroups) {
        if (sub.children && Array.isArray(sub.children)) {
          sub.children = sub.children.filter((child: any) => {
            const name = (child.name || '').toLowerCase();
            const url = (child.url || '').toLowerCase();
            const uid = (child.unique_id || '').toLowerCase();
            return !(
              uid === 'asset_allocation' ||
              url === '/mnt/trans/assets/stock-allocation' ||
              name === 'asset allocation' ||
              name === 'stock allocation'
            );
          });
        }
      }
    }
  }

  items = new BehaviorSubject<any>([]);
}
